import React from 'react';
interface LoadingSpinnerProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    color?: 'primary' | 'white' | 'cyan' | 'slate';
    className?: string;
    text?: string;
}
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
                                                           size = 'md',
                                                           color = 'primary',
                                                           className = '',
                                                           text
                                                       }) => {
    const sizeClasses = {
        sm: 'w-4 h-4 border',
        md: 'w-6 h-6 border-2',
        lg: 'w-8 h-8 border-4',
        xl: 'w-12 h-12 border-4'
    };
    const colorClasses = {
        primary: 'border-cyan-500 border-t-transparent',
        white: 'border-white border-t-transparent',
        cyan: 'border-cyan-400 border-t-transparent',
        slate: 'border-slate-400 border-t-transparent'
    };
    return (
        <div className={`flex flex-col items-center justify-center ${className}`}>
            <div
                className={`
          ${sizeClasses[size]} 
          ${colorClasses[color]} 
          rounded-full animate-spin
        `}
            />
            {text && (
                <p className="mt-3 text-sm text-slate-400 animate-pulse">
                    {text}
                </p>
            )}
        </div>
    );
};
// Loading Overlay Component
interface LoadingOverlayProps {
    isVisible: boolean;
    text?: string;
    backdrop?: boolean;
}
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
                                                                  isVisible,
                                                                  text = 'Cargando...',
                                                                  backdrop = true
                                                              }) => {
    if (!isVisible) return null;
    return (
        <div className={`loading-overlay ${backdrop ? 'modal-backdrop' : ''}`}>
            <div className="loading-card">
                <LoadingSpinner size="lg" color="cyan" text={text} />
            </div>
        </div>
    );
};
// Loading Card Component
interface LoadingCardProps {
    title?: string;
    description?: string;
    className?: string;
}
export const LoadingCard: React.FC<LoadingCardProps> = ({
                                                            title = 'Cargando',
                                                            description,
                                                            className = ''
                                                        }) => {
    return (
        <div className={`loading-card ${className}`}>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl mb-4 shadow-2xl">
                <LoadingSpinner size="md" color="white" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
            {description && (
                <p className="text-slate-400 text-sm">{description}</p>
            )}
        </div>
    );
};
// Button Loading State
interface LoadingButtonProps {
    isLoading: boolean;
    children: React.ReactNode;
    loadingText?: string;
    className?: string;
    disabled?: boolean;
    [key: string]: any;
}
export const LoadingButton: React.FC<LoadingButtonProps> = ({
                                                                isLoading,
                                                                children,
                                                                loadingText = 'Cargando...',
                                                                className = '',
                                                                disabled,
                                                                ...props
                                                            }) => {
    return (
        <button
            {...props}
            disabled={isLoading || disabled}
            className={`btn ${className} ${isLoading ? 'pointer-events-none' : ''}`}
        >
            {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                    <LoadingSpinner size="sm" color="white" />
                    {loadingText}
                </div>
            ) : (
                children
            )}
        </button>
    );
};
// Table Loading State
export const TableLoading: React.FC = () => {
    return (
        <div className="animate-pulse space-y-4 p-6">
            {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                    <div className="rounded-full bg-slate-700 h-10 w-10"></div>
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-700 rounded w-3/4"></div>
                        <div className="h-4 bg-slate-700 rounded w-1/2"></div>
                    </div>
                </div>
            ))}
        </div>
    );
};
// Progress Bar Component
interface ProgressBarProps {
    progress: number;
    className?: string;
    showPercentage?: boolean;
    color?: 'primary' | 'success' | 'warning' | 'danger';
}
export const ProgressBar: React.FC<ProgressBarProps> = ({
                                                            progress,
                                                            className = '',
                                                            showPercentage = true,
                                                            color = 'primary'
                                                        }) => {
    const colorClasses = {
        primary: 'from-cyan-500 to-blue-500',
        success: 'from-emerald-500 to-green-500',
        warning: 'from-amber-500 to-orange-500',
        danger: 'from-red-500 to-red-600'
    };
    const clampedProgress = Math.min(100, Math.max(0, progress));
    return (
        <div className={`w-full ${className}`}>
            <div className="flex justify-between items-center mb-2">
                {showPercentage && (
                    <span className="text-sm text-slate-400">{Math.round(clampedProgress)}%</span>
                )}
            </div>
            <div className="progress-bar">
                <div
                    className={`progress-fill bg-gradient-to-r ${colorClasses[color]}`}
                    style={{ width: `${clampedProgress}%` }}
                />
            </div>
        </div>
    );
};
export default LoadingSpinner;